import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import objectPath from 'object-path';
import { rem } from 'polished';
import screen from 'superior-mq';
import { bp } from 'Styles/helpers';
import Section from '../../section';
import Container from '../../container';
import Subhead from '../../subhead';
import LazyImg from '../../lazy-img';
import TextWave from '../../text-wave';
import HomeH3 from '../HomeH3';
import Num from '../../num';
import Button from '../../button';
import precedingZero from '../../../lib/precendingZero';
import { Link } from '../../../routes';

const lazyImgStyles = active => `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: ${active ? 1 : 0};
  transition: opacity .5s ease-in;
`;

export default class Partners extends PureComponent {

  static propTypes = {
    subheading: PropTypes.string,
    title: PropTypes.string,
    partnerList: PropTypes.arrayOf(PropTypes.object),
  }

  state = {
    activePartner: 0,
  };

  updateActivePartner = (index) => {
    if (window.innerWidth > 766) {
      this.setState({
        activePartner: index,
      });
    }
  }

  render() {
    const { partnerList, title, subheading } = this.props;
    const { activePartner } = this.state;

    const imageList = partnerList.map(partner => (
      objectPath.has(partner, 'fields.partnerFeaturedPhoto.fields') && partner.fields.partnerFeaturedPhoto.fields
    ));

    return (
      <Section>
        <Container>
          <Subhead>{subheading}</Subhead>

          <PartnerGrid>

            <PartnerImg>
              <TextWave>
                <HomeH3>{title}</HomeH3>
              </TextWave>
              <LazyImgWrap>
                {imageList.map((img, i) => {
                  if (!img || (img && !img.file)) return null;

                  return (
                    <LazyImg
                      width={466}
                      height={710}
                      src={`${img.file.url}?h=710&w=466&q=80&fm=jpg&fl=progressive&fit=thumb`}
                      alt={img.description}
                      key={img.file.fileName}
                      styles={lazyImgStyles(i === activePartner)}
                    />
                  );
                })}
              </LazyImgWrap>
            </PartnerImg>

            {partnerList.map((partner, i) => (
              <LogoItem
                i={i}
                key={partner.sys.id}
                onMouseEnter={() => { this.updateActivePartner(i); }}
              >
                <Link route={`/partners/${partner.fields.partnerSlug}`}>
                  <a style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
                    <PartnerNum fontSize={18} i={i}>
                      {precedingZero(i + 1)}
                    </PartnerNum>
                    <LogoWrap>

                      {objectPath.has(partner, 'fields.partnerLogo.fields.file.url') &&
                        <Logo
                          src={partner.fields.partnerLogo.fields.file.url}
                          alt={`${partner.fields.partnerName} logo`}
                        />
                      }

                    </LogoWrap>
                    <Title>
                      {partner.fields.featuredProjectName}
                    </Title>
                  </a>
                </Link>
              </LogoItem>
            ))}

            <PartnerButton href="/partners#become-a-partner" black>Become A Partner</PartnerButton>

          </PartnerGrid>

          <ViewAll>
            <Link route="/partners">
              <a>View All</a>
            </Link>
          </ViewAll>

        </Container>
      </Section>
    );
  }
}

const LazyImgWrap = styled.div`
  position: relative;
  padding-bottom: ${(710 / 466) * 100}%;

  ${screen.below(bp.mobile, `
    display: none;

    img { display: none; }
  `)}
`;

const PartnerGrid = styled.div`
  display: grid;
  grid-column-gap: 20px;
  grid-row-gap: 40px;
  grid-template-columns: repeat(24, 1fr);
  grid-template-rows: repeat(2, 1fr) auto;

  ${screen.below(bp.laptopSm, `
    grid-column-gap: 10px;
    grid-row-gap: 20px;
  `)}

  ${screen.below(bp.mobile, `
    grid-row-gap: 40px;
    grid-column-gap: 40px;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto repeat(2, 1fr) auto;
  `)}
`;

const PartnerImg = styled.div`
  position: relative;
  grid-column: 9 / span 8;
  grid-row: 1 / span 2;

  ${screen.below(bp.portrait, `
    grid-column: 8 / span 10;
  `)}

  ${screen.below(bp.mobile, `
    grid-column: 1 / span 2;
    grid-row: 1;
  `)}

  ${HomeH3} {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    white-space: nowrap;
    z-index: 1;

    ${screen.below(bp.laptop, `
      font-size: ${rem(130)};
    `)}

    ${screen.below(bp.laptopSm, `
      font-size: ${rem(80)};
    `)}

    ${screen.below(bp.mobile, `
      position: static;
      transform: none;
      text-align: center;
      font-size: ${rem(60)};
    `)}
  }
`;

const LogoItem = styled.div`
  position: relative;
  padding: 20px 0 32px 32px;
  grid-column: ${props => (props.i + 1) % 2 === 0 ? 18 : 3} / span 5;
  align-self: ${props => props.i > 1 ? 'end' : 'start'};

  ${css`
    ${props => screen.below(bp.desktop, `
      padding-bottom: 0;
      padding-top: 0;
      grid-column: ${(props.i + 1) % 2 === 0 ? 18 : 2} / span 6;
    `)}
  `}

  ${screen.between(bp.portrait, bp.laptop, `
    align-self: center;
  `)}

  ${css`
    ${props => screen.below(bp.portrait, `
      padding: 0;
      grid-column: ${(props.i + 1) % 2 === 0 ? 19 : 1} / span 6;
    `)}
  `}

  ${screen.below(bp.mobile, `
    padding-top: 30px;
    grid-column: span 1;
    grid-row: auto;
  `)}
`;

const PartnerNum = styled(Num)`
  position: absolute;
  bottom: 75px;
  left: -12px;
  color: ${props => props.theme.blue};

  ${screen.below(bp.desktop, `
     bottom: 30px;
  `)}

  ${screen.below(bp.laptopSm, `
     left: -2px;
  `)}

  ${screen.below(bp.laptopSm, `
    top: -6px;
    bottom: auto;
    left: 0;
  `)}
`;

const LogoWrap = styled.div`
  position: relative;
  max-width: 240px;
  margin: auto;

  &::after {
    content: "";
    padding-bottom: ${(140 / 240) * 100}%;
    display: block;
  }

  ${screen.below(bp.laptop, `
    width: 60%;
  `)}
`;

const Logo = styled.img`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  max-width: 58%;
  max-height: 100%;
  transform: translate(-50%, -50%);

  ${screen.below(bp.laptop, `
    max-width: 80%;
  `)}

  ${screen.below(bp.laptopSm, `
    max-width: 90%;
  `)}
`;

const Title = styled.h4`
  padding-top: 24px;
  margin-top: 40px;
  font-family: ${props => props.theme.sansFont};
  font-size: ${rem(18)};
  border-top: 5px solid ${props => props.theme.blue};

  ${screen.below(bp.desktop, `
    margin-top: 20px;
    padding-top: 20px;
  `)}

  ${screen.below(bp.portrait, `
    padding-top: 10px;
    font-size: 1rem;
  `)}
`;

const PartnerButton = styled(Button)`
  grid-column: 9 / span 8;
  width: 100%;
  min-width: 100%;

  ${screen.below(bp.portrait, `
    grid-column 8 / span 10;
  `)}

  ${screen.below(bp.mobile, `
    grid-column 1 / span 2;
    margin-top: 40px;
  `)}
`;

const ViewAll = styled.div`
  text-align: center;
  margin-top: 80px;

  a {
    font-weight: 600;
    text-decoration: underline;
    color: ${props => props.theme.blue};
  }

  ${screen.below(bp.laptopSm, `
    margin-top: 50px;
  `)}

  ${screen.below(bp.laptopSm, `
    margin-top: 20px;
  `)}
`;
